import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminListOrders, adminUpdateOrder } from '../../lib/shopApi';
import { fmt } from '../../lib/shopPricing';
import usePageMeta from '../../hooks/usePageMeta';

// Token-gated order list. The token is SHOP_ADMIN_TOKEN on the backend; it is
// kept in sessionStorage only. n8n drives the same status endpoints.
const STATUSES = ['new', 'held', 'supplier_ordered', 'shipped', 'delivered', 'install_booked', 'installed', 'refunded', 'cancelled'];
const KEY = 'sd_admin_token';

const AdminOrders = () => {
  usePageMeta();
  const [token, setToken] = useState(() => {
    try {
      return window.sessionStorage.getItem(KEY) || '';
    } catch {
      return '';
    }
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tracking, setTracking] = useState({});

  const load = async (t = token) => {
    if (!t) return;
    setLoading(true);
    setError('');
    try {
      const data = await adminListOrders(t);
      setOrders(data.orders || []);
      try {
        window.sessionStorage.setItem(KEY, t);
      } catch {
        /* ignore */
      }
    } catch (err) {
      setError(err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = async (orderNo, status) => {
    try {
      await adminUpdateOrder(token, orderNo, { status, tracking: tracking[orderNo] || undefined });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Shop orders</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="flex flex-wrap gap-3 items-end mb-6"
        >
          <div className="w-80">
            <label htmlFor="admin-token" className="text-sm text-gray-700">
              Admin token
            </label>
            <Input id="admin-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" />
          </div>
          <Button type="submit" className="bg-gray-900 text-white" disabled={loading}>
            {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw className="mr-2" size={16} />} Load
          </Button>
        </form>
        {error && <p className="text-sm text-red-700 mb-4">{error}</p>}
        {orders.length > 0 && (
          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="p-3">Order</th>
                  <th className="p-3">When</th>
                  <th className="p-3">Customer</th>
                  <th className="p-3">Items</th>
                  <th className="p-3">Total</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.orderNo} className="border-t border-gray-200 align-top">
                    <td className="p-3 font-mono">{o.orderNo}</td>
                    <td className="p-3 text-gray-600 whitespace-nowrap">{new Date(o.createdAt).toLocaleString('en-NZ')}</td>
                    <td className="p-3">
                      <div className="text-gray-900">{o.contact && o.contact.name}</div>
                      <div className="text-gray-600">{o.email}</div>
                      <div className="text-gray-600">{o.contact && o.contact.phone}</div>
                    </td>
                    <td className="p-3 text-gray-700">
                      {o.items.map((it) => (
                        <div key={`${it.variantId}-${it.backsetMm || ''}`}>
                          {it.qty} × {it.shortName} ({it.variantLabel}
                          {it.backsetMm ? `, ${it.backsetMm} mm` : ''}) · {it.supplier}
                        </div>
                      ))}
                      {o.install && (
                        <div className="text-red-700 font-semibold">
                          Install: {o.install.tierLabel} · {o.install.suburb}
                        </div>
                      )}
                      {o.photosStatus && o.photosStatus !== 'n/a' && (
                        // Photos come after payment; the reminder ladder runs itself (day 2/5/10).
                        <div className={`text-xs mt-1 ${o.photosStatus === 'received' ? 'text-green-700' : 'text-amber-700'}`} data-testid="admin-photos">
                          {o.photosStatus === 'received'
                            ? `Photos: ${o.photoCount} received`
                            : `Photos: outstanding · ${(o.photoReminders || []).filter((r) => !r.skipped).length} reminder${(o.photoReminders || []).filter((r) => !r.skipped).length === 1 ? '' : 's'} sent`}
                        </div>
                      )}
                      {o.poNumbers && o.poNumbers.length > 0 && <div className="text-gray-500">PO {o.poNumbers.join(', ')}</div>}
                      {o.tracking && <div className="text-gray-500">Tracking {o.tracking}</div>}
                    </td>
                    <td className="p-3 whitespace-nowrap">{fmt(o.totalCents)}</td>
                    <td className="p-3">
                      <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold">{o.status}</span>
                      <div className="text-xs text-gray-500 mt-1">{o.payment}</div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col gap-2">
                        <Input placeholder="Tracking no." value={tracking[o.orderNo] || ''} onChange={(e) => setTracking((t) => ({ ...t, [o.orderNo]: e.target.value }))} className="h-8 text-xs" />
                        <select value={o.status} onChange={(e) => update(o.orderNo, e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-xs bg-white">
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
